/**
 * optional-products.js
 *
 * V15 native add-to-cart architecture.
 *
 * The PDP bundle configurator remains the source of truth, but the machine is
 * added through the native theme product form. This file only:
 * - writes one explicit _faber_discount hidden property into the native product form;
 * - adds selected bundle optionals via /cart/add.js only when they are missing;
 * - prevents re-adding the machine when the customer comes back to the PDP and
 *   only wants to add a missing bundle component.
 */

(() => {
  let busy = false;
  let lastNativeAdd = 0;
  let latestCart = null;

  const SELECTOR_FORMS = 'form[action*="/cart/add"]';
  const SELECTOR_OPTIONAL_CHECKED = '.js-optional:checked:not(:disabled)';
  const BUNDLE_ROOT = '[data-rpd-bundle-summary]';
  const HIDDEN_MARKER = 'data-faber-bundle-property';

  function hasBundleConfigurator() {
    return !!document.querySelector(BUNDLE_ROOT);
  }

  function getBundleState() {
    return window.__faberRpdBundleState || null;
  }

  function getCheckedOptionalInputs() {
    return Array.from(document.querySelectorAll(SELECTOR_OPTIONAL_CHECKED))
      .filter((el) => Number.isFinite(Number(el.value)) && Number(el.value) > 0);
  }

  function getMainVariantIdFromForm(form) {
    const idInput = form.querySelector('input[name="id"]');
    const id = Number(idInput?.value);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  function getDrawerSectionIds() {
    const drawer = document.querySelector('cart-drawer-component');
    const raw = drawer?.getAttribute('sections') || drawer?.dataset?.sections || '';
    if (raw) return raw.split(',').map((s) => s.trim()).filter(Boolean);

    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    return sections.length ? sections : ['cart-drawer', 'cart-icon-bubble'];
  }

  function bundleId() {
    const existing = getBundleState()?.bundleId;
    if (existing) return String(existing);
    try {
      return `faber-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
    } catch (e) {
      return `faber-${Date.now()}`;
    }
  }

  function localeCode() {
    const lang = (document.documentElement.lang || '').toLowerCase();
    return lang.startsWith('it') ? 'it' : 'en';
  }

  function pct(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return '0';
    return String(Number(n.toFixed(2))).replace(/\.0$/, '');
  }

  function cleanText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function optionalType(input) {
    const explicit = String(input.dataset.rpdType || input.dataset.addonType || '').toLowerCase().trim();
    if (explicit === 'pods150' || explicit === 'coffee') return 'coffee';
    if (explicit === 'accessory' || explicit === 'barmat') return 'accessory';

    const item = input.closest('.product-optionals__item,label') || input.parentElement;
    const text = [
      input.dataset.title,
      input.dataset.productTitle,
      input.dataset.sku,
      input.dataset.productSku,
      item?.textContent,
    ].filter(Boolean).join(' ').toLowerCase();

    if (text.includes('barmat') || text.includes('bar mat') || text.includes('bottiglia') || text.includes('vetro') || text.includes('cavo') || text.includes('svizzera') || text.includes('swiss')) {
      return 'accessory';
    }

    if (text.includes('ciald') || text.includes('caff') || text.includes('coffee') || text.includes('150 pz') || text.includes('150pz')) {
      return 'coffee';
    }

    return 'accessory';
  }

  function optionalTitle(input) {
    const item = input.closest('.product-optionals__item,label') || input.parentElement;
    return cleanText(
      input.dataset.title ||
      input.dataset.productTitle ||
      item?.querySelector('.product-optionals__name,[data-rpd-optional-title]')?.textContent ||
      item?.textContent ||
      ''
    );
  }

  function machineBundleLabel(state) {
    const discount = pct(state?.machineDiscount || 0);
    if (localeCode() === 'it') return `Faber Bundle macchina → ${discount}%`;
    return `Faber machine bundle → ${discount}%`;
  }

  function discountToken(kind, discount) {
    const value = pct(discount || 0).replace('.', '_');
    if (!value || value === '0') return '';
    return `${kind}_${value}`;
  }

  function machineProperties(id, state) {
    const discount = pct(state?.machineDiscount || 0);
    const token = discountToken('machine', discount);

    // Only lines that must receive a Shopify Function discount get _faber_discount.
    // Everything else stays clean, so standalone/catalog rules cannot be confused
    // with PDP bundle lines.
    if (!token) return {};

    return {
      _faber_discount: token,
      _faber_bundle: '1',
      _faber_bundle_id: id,
      _faber_bundle_label: machineBundleLabel(state),
    };
  }

  function optionalDiscount(input, role) {
    const state = getBundleState();
    const addon = (state?.addons || []).find((item) => String(item.id) === String(input.value));

    // Use exactly the same discount that the validated PDP summary uses.
    // rpd-bundle-v6.js calculates optional discounts as:
    // - catalog discount when the upgrade is not selected;
    // - bundle discount when the upgrade is selected.
    if (addon) {
      const value = state?.hasUpgrade ? addon.bundle : addon.catalog;
      const n = Number(value);
      if (Number.isFinite(n) && n > 0) return pct(n);
    }

    const direct = Number(input.dataset.rpdCurrentDiscount || input.dataset.currentDiscount || '');
    if (Number.isFinite(direct) && direct > 0) return pct(direct);

    const catalog = Number(input.dataset.rpdCatalogDiscount || input.dataset.catalogDiscount || '');
    if (Number.isFinite(catalog) && catalog > 0) return pct(catalog);

    // Safe defaults for standalone/catalog promo values.
    return role === 'coffee' ? '20' : '10';
  }

  function optionalProperties(input, id) {
    const role = optionalType(input);
    const discount = optionalDiscount(input, role);
    const labelIt = role === 'coffee' ? `Caffè nel bundle macchina → ${discount}%` : `Accessori nel bundle macchina → ${discount}%`;
    const labelEn = role === 'coffee' ? `Coffee in machine bundle → ${discount}%` : `Accessories in machine bundle → ${discount}%`;

    const token = discountToken(role, discount);
    const props = {
      _faber_bundle: '1',
      _faber_bundle_id: id,
      _faber_bundle_label: localeCode() === 'it' ? labelIt : labelEn,
    };
    if (token) props._faber_discount = token;
    return props;
  }

  function ensureHiddenProperty(form, name, value) {
    let input = form.querySelector(`input[name="properties[${CSS.escape(name)}]"][${HIDDEN_MARKER}="1"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.setAttribute(HIDDEN_MARKER, '1');
      form.appendChild(input);
    }
    input.value = String(value ?? '');
  }

  function removeHiddenProperties(form) {
    form.querySelectorAll(`input[${HIDDEN_MARKER}="1"]`).forEach((input) => input.remove());
  }

  function syncNativeFormProperties() {
    if (!hasBundleConfigurator()) return;
    const state = getBundleState();
    const id = bundleId();

    document.querySelectorAll(SELECTOR_FORMS).forEach((form) => {
      const mainVariantId = getMainVariantIdFromForm(form);
      if (!mainVariantId) return;

      removeHiddenProperties(form);
      const props = machineProperties(id, state);
      Object.entries(props).forEach(([key, value]) => ensureHiddenProperty(form, key, value));
    });
  }

  async function getCart(force = false) {
    if (!force && latestCart) return latestCart;
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      latestCart = res.ok ? await res.json() : null;
      return latestCart;
    } catch (e) {
      return null;
    }
  }

  function hasVariant(cart, variantId) {
    return (cart?.items || []).some((item) => String(item.variant_id) === String(variantId));
  }

  function missingOptionalInputs(cart) {
    return getCheckedOptionalInputs().filter((input) => !hasVariant(cart, input.value));
  }

  async function cartAdd(items) {
    const sections = getDrawerSectionIds();
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items,
        sections,
        sections_url: window.location.pathname,
      }),
    });

    const json = await res.json().catch(() => ({}));

    if (!res.ok) {
      console.error('cart/add.js failed', res.status, json);
      throw new Error(`cart_add_failed_${res.status}`);
    }

    latestCart = null;
    return json;
  }

  function dispatchCartUpdate(cartResponse, sourceId = 'optional-products') {
    const ev = new Event('cart:update', { bubbles: true });
    ev.detail = {
      resource: cartResponse,
      sourceId,
      data: {
        source: 'optional-products',
        didError: false,
        itemCount: cartResponse?.item_count,
        sections: cartResponse?.sections,
      },
    };
    document.dispatchEvent(ev);
    document.dispatchEvent(new CustomEvent('rpd:bundle:added', { bubbles: true, detail: { cart: cartResponse } }));
  }

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    document.dispatchEvent(new Event('cart:open', { bubbles: true }));
  }

  async function addMissingOptionals(cart) {
    if (!hasBundleConfigurator() || busy) return false;

    const missing = missingOptionalInputs(cart || (await getCart(true)));
    if (!missing.length) return false;

    busy = true;
    try {
      const id = bundleId();
      const items = missing.map((input) => ({
        id: Number(input.value),
        quantity: 1,
        properties: optionalProperties(input, id),
      }));

      const cartResponse = await cartAdd(items);
      dispatchCartUpdate(cartResponse);
      openCartDrawer();
      return true;
    } catch (err) {
      console.error('Bundle optional add failed:', err);
      alert('Errore durante l’aggiunta al carrello. Riprova.');
      return true;
    } finally {
      busy = false;
    }
  }

  async function maybeOnlyAddMissingPieces(event, form, submitter) {
    if (!hasBundleConfigurator()) return false;

    // We block the original click first, then deliberately hand control back to
    // the native product form only when the machine is not already in cart. This
    // avoids the race where Shopify adds the machine again before /cart.js has
    // answered.
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    syncNativeFormProperties();

    const mainVariantId = getMainVariantIdFromForm(form);
    if (!mainVariantId) return true;

    const cart = await getCart(true);
    const machineAlreadyInCart = hasVariant(cart, mainVariantId);
    const missing = missingOptionalInputs(cart);

    if (machineAlreadyInCart) {
      if (missing.length) {
        await addMissingOptionals(cart);
      } else {
        openCartDrawer();
      }
      return true;
    }

    // Machine is not in cart: submit the same form through the theme-native
    // ProductFormComponent. The hidden _faber_* inputs are already in the form.
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitter || undefined);
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }

    return true;
  }

  function bindForm(form) {
    if (form.dataset.faberNativeBundleBound === '1') return;
    form.dataset.faberNativeBundleBound = '1';

    form.addEventListener(
      'submit',
      (event) => {
        // Do not await before ProductFormComponent sees the submit unless we
        // actually need to block a duplicate machine add. The click capture below
        // has already prefetched the cart in most cases.
        syncNativeFormProperties();
      },
      true
    );

    form.querySelectorAll('[type="submit"], button[name="add"]').forEach((btn) => {
      btn.addEventListener(
        'click',
        async (event) => {
          if (!hasBundleConfigurator()) return;
          if (busy) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation?.();
            return;
          }

          await maybeOnlyAddMissingPieces(event, form, btn);
        },
        true
      );
    });
  }

  function bindGlobalNativeCartAdd() {
    document.addEventListener('cart:add', async (event) => {
      if (!hasBundleConfigurator()) return;
      if (Date.now() - lastNativeAdd < 350) return;
      lastNativeAdd = Date.now();

      const cart = event.detail?.resource?.items ? event.detail.resource : await getCart(true);
      await addMissingOptionals(cart);
    });

    // Some builds dispatch only cart:update after add.
    document.addEventListener('cart:update', async (event) => {
      if (!hasBundleConfigurator()) return;
      if (event.detail?.data?.source !== 'product-form-component') return;
      if (Date.now() - lastNativeAdd < 350) return;
      lastNativeAdd = Date.now();

      const cart = event.detail?.resource?.items ? event.detail.resource : await getCart(true);
      await addMissingOptionals(cart);
    });
  }

  function init() {
    syncNativeFormProperties();
    document.querySelectorAll(SELECTOR_FORMS).forEach(bindForm);
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('variant:update', () => setTimeout(init, 50));
  document.addEventListener('rpd:bundle:update', () => setTimeout(syncNativeFormProperties, 0));
  document.addEventListener('change', (event) => {
    if (event.target?.matches?.('.js-optional,[name="quantity"]') || event.target?.closest?.('[data-role="upgrade-options"]')) {
      setTimeout(syncNativeFormProperties, 60);
    }
  });

  bindGlobalNativeCartAdd();
  if (document.readyState !== 'loading') init();
})();

// ---------------------------------
// Optional description "read more"
// Localized via data-more/data-less
// ---------------------------------
document.addEventListener('click', (e) => {
  const btn = e.target.closest?.('.product-optionals__desc-toggle');
  if (!btn) return;

  e.preventDefault();
  e.stopPropagation();

  const item = btn.closest('.product-optionals__item');
  if (!item) return;

  const expanded = item.classList.toggle('is-desc-expanded');
  btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');

  const more = btn.dataset.more || 'Leggi di più';
  const less = btn.dataset.less || 'Mostra meno';
  btn.textContent = expanded ? less : more;
});
